import React, { useState } from "react";
import * as XLSX from "xlsx";
import { jsPDF } from "jspdf";
import { autoTable } from "jspdf-autotable";
import { MdDownload } from "react-icons/md";
import toast from "react-hot-toast";
import "../styles/exportMenu.css";

/**
 * Excel / PDF export for a filtered list.
 *
 * `fetchAll` must return every row matching the current filters, not just the
 * page on screen - the point of the export is to hand the client a full date
 * range, so exporting the visible page would quietly give them the wrong file.
 *
 * `columns` is [{ header, value(row, index) }], shared by both formats so the
 * spreadsheet and the PDF can never drift apart.
 */
const ExportMenu = ({
  fetchAll,
  columns,
  fileName,
  title,
  subtitle,
  disabled,
  orientation = "landscape",
}) => {
  const [busy, setBusy] = useState(null);
  const [open, setOpen] = useState(false);

  const collect = async () => {
    const data = await fetchAll();
    if (!data?.length) {
      toast.error("No data to export for the selected filters");
      return null;
    }
    return data;
  };

  const downloadExcel = async () => {
    setBusy("excel");
    setOpen(false);
    try {
      const data = await collect();
      if (!data) return;

      const rows = data.map((row, index) => {
        const out = {};
        columns.forEach((c) => {
          out[c.header] = c.value(row, index);
        });
        return out;
      });

      const ws = XLSX.utils.json_to_sheet(rows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, title.slice(0, 31));
      XLSX.writeFile(wb, `${fileName}.xlsx`);
      toast.success(`Excel downloaded (${rows.length} rows)`);
    } catch (err) {
      console.error("Excel export error:", err);
      toast.error(err?.message || "Failed to download Excel");
    } finally {
      setBusy(null);
    }
  };

  const downloadPDF = async () => {
    setBusy("pdf");
    setOpen(false);
    try {
      const data = await collect();
      if (!data) return;

      const doc = new jsPDF({ orientation, unit: "mm" });
      const pageWidth = doc.internal.pageSize.getWidth();

      doc.setFontSize(14);
      doc.text(title, pageWidth / 2, 15, { align: "center" });
      if (subtitle) {
        doc.setFontSize(10);
        doc.text(subtitle, pageWidth / 2, 22, { align: "center" });
      }

      autoTable(doc, {
        startY: subtitle ? 28 : 22,
        head: [columns.map((c) => c.header)],
        body: data.map((row, index) =>
          columns.map((c) => String(c.value(row, index) ?? "")),
        ),
        styles: { fontSize: 8, cellPadding: 2 },
        headStyles: { fillColor: [29, 78, 216], textColor: 255 },
        alternateRowStyles: { fillColor: [248, 250, 252] },
      });

      doc.save(`${fileName}.pdf`);
      toast.success(`PDF downloaded (${data.length} rows)`);
    } catch (err) {
      console.error("PDF export error:", err);
      toast.error(err?.message || "Failed to download PDF");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="export-menu">
      <button
        type="button"
        className="export-btn"
        disabled={disabled || !!busy}
        onClick={() => setOpen((v) => !v)}
      >
        <MdDownload size={17} />
        {busy === "excel"
          ? "Exporting..."
          : busy === "pdf"
            ? "Exporting..."
            : "Export"}
      </button>

      {open && !busy && (
        <>
          {/* Click-away layer so the menu closes without a document listener. */}
          <div className="export-backdrop" onClick={() => setOpen(false)} />
          <div className="export-dropdown">
            <button type="button" onClick={downloadExcel}>
              Excel (.xlsx)
            </button>
            <button type="button" onClick={downloadPDF}>
              PDF (.pdf)
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default ExportMenu;
